/**
 * Edge-safe logger.
 *
 * This module is imported transitively by the edge entrypoint
 * (`@morphllm/morphsdk/edge`) via fastapply, modelrouter, etc.
 * Edge runtimes (Vercel Edge Functions, Cloudflare Workers, Deno Deploy)
 * run on V8 isolates — not Node.js — so Node built-ins like fs don't
 * exist. A top-level static import of fs would crash at module-load time,
 * even if createWriteStream is only called conditionally.
 *
 * Fix: we use a dynamic import() behind a runtime check. In Node the
 * import resolves and file logging works normally. In edge runtimes the
 * import rejects and we silently fall back to console-only logging.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface WriteStream {
  write(chunk: string): boolean;
}

class MorphLogger {
  private enabled: boolean;
  private fileStream: WriteStream | null;
  /** Resolves once the file stream is initialized (or immediately if no file logging). */
  readonly ready: Promise<void>;

  constructor() {
    this.enabled = typeof process !== 'undefined' &&
      (process.env.MORPH_DEBUG === '1' || !!process.env.MORPH_LOG_FILE);
    this.fileStream = null;

    const f = typeof process !== 'undefined' ? process.env.MORPH_LOG_FILE : undefined;
    if (f) {
      // Dynamic import — never evaluated at parse time, so edge runtimes
      // don't blow up with "Module 'fs' not found".
      this.ready = import('fs')
        .then((fs) => {
          this.fileStream = fs.createWriteStream(f, { flags: 'a' });
        })
        .catch(() => {
          // Edge runtime — fs unavailable, silently skip file logging
        });
    } else {
      this.ready = Promise.resolve();
    }
  }

  debug(component: string, msg: string, data?: Record<string, unknown>) { this._log('debug', component, msg, data); }
  info(component: string, msg: string, data?: Record<string, unknown>) { this._log('info', component, msg, data); }
  warn(component: string, msg: string, data?: Record<string, unknown>) { this._log('warn', component, msg, data); }
  error(component: string, msg: string, data?: Record<string, unknown>) { this._log('error', component, msg, data); }

  enable() { this.enabled = true; }
  get isEnabled() { return this.enabled; }

  private _log(level: LogLevel, component: string, msg: string, data?: Record<string, unknown>) {
    if (level !== 'error' && !this.enabled) return;
    const ts = new Date().toISOString();
    const prefix = `[${ts}] [${level.toUpperCase()}] [${component}]`;
    console.error(data ? `${prefix} ${msg} ${JSON.stringify(data)}` : `${prefix} ${msg}`);
    this.fileStream?.write(JSON.stringify({ ts, level, component, msg, ...(data && { data }) }) + '\n');
  }
}

export const logger = new MorphLogger();
